import { useEffect, useState } from "react";
import { DollarSign, Check, X, FileDown, Edit3 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";
import { Button } from "@/components/ui/button";
import PayoutRunsPanel from "@/pages/admin/PayoutRunsPanel";

const STATUS_PILL = {
  pending:   { bg: "bg-amber-100",   text: "text-amber-800",   label: "Pending" },
  approved:  { bg: "bg-cyan-100",    text: "text-cyan-800",    label: "Approved" },
  paid:      { bg: "bg-emerald-100", text: "text-emerald-800", label: "Paid" },
  rejected:  { bg: "bg-rose-100",    text: "text-rose-800",    label: "Rejected" },
  cancelled: { bg: "bg-slate-100",   text: "text-slate-700",   label: "Cancelled" },
  flagged:   { bg: "bg-orange-100",  text: "text-orange-800",  label: "Flagged" },
};

const KIND_LABEL = {
  water_test: "Water test", membership: "Membership",
  property_transfer: "Transfer", service: "Service",
};

const fmt = (n) => `$${(n || 0).toFixed(2)}`;

export default function AdminCommissions() {
  const [dash, setDash] = useState(null);
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = () => {
    const params = statusFilter ? { status: statusFilter } : {};
    Promise.all([
      api.get("/admin/commissions/dashboard").then(({ data }) => setDash(data)),
      api.get("/admin/commissions", { params }).then(({ data }) => { setRows(data); setSelected(new Set()); }),
    ]);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const toggleSel = (id) => {
    const ns = new Set(selected);
    ns.has(id) ? ns.delete(id) : ns.add(id);
    setSelected(ns);
  };

  const act = async (cid, path) => {
    setBusy(true);
    try {
      await api.post(`/admin/commissions/${cid}/${path}`, { notes: "" });
      toast.success("Updated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const adjust = async (cid) => {
    const r = window.prompt("New commission rate (0–1, e.g. 0.15 for 15%):");
    if (!r) return;
    const rate = parseFloat(r);
    if (isNaN(rate) || rate < 0 || rate > 1) { toast.error("Rate must be 0–1"); return; }
    try {
      await api.post(`/admin/commissions/${cid}/adjust`, { new_rate: rate, notes: `Adjusted to ${(rate*100).toFixed(0)}%` });
      toast.success("Rate adjusted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const bulk = async (action) => {
    if (selected.size === 0) { toast.error("Select commissions first"); return; }
    if (!window.confirm(`${action} ${selected.size} commission(s)?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/commissions/bulk", { ids: Array.from(selected), action });
      toast.success(`${data.succeeded} updated · ${data.failed.length} failed`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk failed");
    } finally { setBusy(false); }
  };

  const dlStatement = async (partnerId) => {
    const token = localStorage.getItem("scwt_token");
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/commissions/statement.pdf?partner_id=${partnerId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) return toast.error("Statement failed");
    const blob = await r.blob();
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `earnings-statement.pdf`; a.click();
  };

  return (
    <PortalShell>
      <div data-testid="admin-commissions-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-emerald-600">Partner commissions</div>
            <h1 className="font-display mt-2 text-3xl font-bold text-slate-900">Commission ledger</h1>
            <p className="text-slate-500 mt-1 text-sm">Default rates · water test {(dash?.default_rates?.water_test*100 || 10).toFixed(0)}% · membership {(dash?.default_rates?.membership*100 || 20).toFixed(0)}%. Admins approve and mark paid.</p>
          </div>
        </div>

        {dash && (
          <div className="mt-7 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="commission-metrics">
            {["pending", "approved", "paid", "flagged"].map((s) => {
              const b = dash.by_status[s] || { count: 0, amount: 0 };
              const cfg = STATUS_PILL[s];
              return (
                <div key={s} className="rounded-3xl bg-white border border-slate-200 p-5">
                  <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${cfg.bg} ${cfg.text}`}>{cfg.label}</div>
                  <div className="font-display text-3xl font-bold text-slate-900 mt-2">{fmt(b.amount)}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{b.count} commission{b.count !== 1 ? "s" : ""}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* Filters + bulk */}
        <div className="mt-7 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 text-xs">
            <select data-testid="comm-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-full border-slate-200 text-xs px-3 py-1.5">
              <option value="">All statuses</option>
              {Object.entries(STATUS_PILL).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <span className="text-slate-500">{selected.size} selected</span>
          </div>
          {selected.size > 0 && (
            <div className="flex gap-2" data-testid="bulk-actions">
              <Button onClick={() => bulk("approve")} disabled={busy} size="sm" className="rounded-full bg-cyan-700 hover:bg-cyan-600 text-white text-xs">Bulk approve</Button>
              <Button onClick={() => bulk("mark_paid")} disabled={busy} size="sm" className="rounded-full bg-emerald-700 hover:bg-emerald-600 text-white text-xs">Bulk mark paid</Button>
              <Button onClick={() => bulk("reject")} disabled={busy} size="sm" variant="outline" className="rounded-full text-xs">Bulk reject</Button>
            </div>
          )}
        </div>

        <div className="mt-3 bg-white rounded-3xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                <tr>
                  <th className="px-4 py-3 w-8"></th>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Partner</th>
                  <th className="text-left px-4 py-3">Type</th>
                  <th className="text-right px-4 py-3">Revenue</th>
                  <th className="text-right px-4 py-3">Rate</th>
                  <th className="text-right px-4 py-3">Commission</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan="9" className="text-center py-12 text-slate-500">
                    <DollarSign className="w-7 h-7 mx-auto mb-2 text-slate-300" />
                    No commissions yet. They'll appear when partner-attributed bookings or memberships convert.
                  </td></tr>
                ) : rows.map((c) => {
                  const pill = STATUS_PILL[c.status] || STATUS_PILL.pending;
                  return (
                    <tr key={c.id} data-testid={`commission-row-${c.id}`} className="border-t border-slate-100 hover:bg-slate-50/50">
                      <td className="px-4 py-3"><input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSel(c.id)} className="rounded" /></td>
                      <td className="px-4 py-3 text-xs text-slate-600">{c.earned_at?.slice(0, 10)}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900 flex items-center gap-1.5">
                          {c.partner?.business_name || c.partner?.email}
                          {c.is_test && (
                            <span
                              data-testid={`commission-test-badge-${c.id}`}
                              title="Test/demo record — stamped is_test because the referred booking used the platform QA checkout email. Always excluded from real payout runs and reporting, regardless of status."
                              className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase bg-slate-800 text-white">
                              Test
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-500">{c.property_address || "—"}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{KIND_LABEL[c.revenue_kind] || c.revenue_kind}</td>
                      <td className="px-4 py-3 text-right font-mono">{fmt(c.revenue_amount)}</td>
                      <td className="px-4 py-3 text-right text-slate-600">{(c.commission_rate * 100).toFixed(0)}%</td>
                      <td className="px-4 py-3 text-right font-mono font-bold text-emerald-700">{fmt(c.commission_amount)}</td>
                      <td className="px-4 py-3"><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${pill.bg} ${pill.text}`}>{pill.label}</span></td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-1 items-center">
                          {c.status === "pending" && (
                            <button data-testid={`approve-${c.id}`} onClick={() => act(c.id, "approve")} title="Approve" className="p-1.5 rounded text-cyan-700 hover:bg-cyan-50"><Check className="w-3.5 h-3.5" /></button>
                          )}
                          {(c.status === "pending" || c.status === "approved") && (
                            <button data-testid={`pay-${c.id}`} onClick={() => act(c.id, "mark-paid")} title="Mark paid" className="px-2 py-1 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-bold">PAID</button>
                          )}
                          {c.status !== "paid" && c.status !== "rejected" && (
                            <button data-testid={`reject-${c.id}`} onClick={() => act(c.id, "reject")} title="Reject" className="p-1.5 rounded text-rose-500 hover:bg-rose-50"><X className="w-3.5 h-3.5" /></button>
                          )}
                          <button onClick={() => adjust(c.id)} title="Adjust rate" className="p-1.5 rounded text-slate-500 hover:bg-slate-100"><Edit3 className="w-3.5 h-3.5" /></button>
                          <button onClick={() => dlStatement(c.partner_id)} title="Statement PDF" className="p-1.5 rounded text-slate-500 hover:bg-slate-100"><FileDown className="w-3.5 h-3.5" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <PayoutRunsPanel />
      </div>
    </PortalShell>
  );
}
